import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * useRealtime — connects to the backend WebSocket gateway and listens for events.
 *
 * Args:
 *   handlers: { [eventType]: (data) => void }   — fires when an event arrives
 *
 * Returns: { connected: boolean }
 *
 * Reconnects automatically with backoff. Pings every 25s to keep the
 * connection alive through any ingress idle timeouts.
 */
export function useRealtime(handlers = {}) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  // Keep the latest handlers without re-creating the connection every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // not logged in

    // Build WS URL from the backend HTTP url
    const httpUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl =
      httpUrl.replace(/^http/, 'ws') + `/api/realtime/ws?token=${encodeURIComponent(token)}`;

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      console.error('Realtime WS construction failed:', e);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttemptRef.current = 0;
      // Heartbeat: ingress proxies often drop idle WS after 60s
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      pingTimerRef.current = setInterval(() => {
        try { ws.send('ping'); } catch {}
      }, 25000);
    };

    ws.onmessage = (evt) => {
      if (evt.data === 'pong') return;
      try {
        const msg = JSON.parse(evt.data);
        const type = msg?.type;
        if (!type) return;
        const handler = handlersRef.current?.[type];
        if (typeof handler === 'function') {
          handler(msg.data || {});
        }
        // Also invoke a wildcard handler if provided
        const any = handlersRef.current?.['*'];
        if (typeof any === 'function') any(msg);
      } catch (e) {
        // Ignore non-JSON frames
      }
    };

    ws.onerror = () => {
      // onclose will handle reconnect
    };

    ws.onclose = () => {
      setConnected(false);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      // Reconnect with exponential backoff capped at 30s
      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const delay = Math.min(30000, 500 * 2 ** Math.min(attempt, 6));
      reconnectTimerRef.current = setTimeout(() => connect(), delay);
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
  }, [connect]);

  return { connected };
}

/**
 * useDesktopNotifications — small helper for Notification API.
 * Returns { permission, request, notify }.
 */
export function useDesktopNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const request = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  const notify = useCallback((title, options = {}) => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible' && !options.evenIfVisible) {
      // Don't pop a desktop alert when the user is already looking at the tab
      return;
    }
    try {
      const n = new Notification(title, {
        icon: '/logo.png',
        badge: '/logo.png',
        ...options,
      });
      if (options.onClick) {
        n.onclick = () => {
          window.focus();
          options.onClick();
          n.close();
        };
      }
    } catch (e) {
      // Some browsers throw on icon load — non-fatal
    }
  }, []);

  return { permission, request, notify };
}
